import type { FilterNode, FilterCondition, CsvRow } from '../types';
import { getFilterRoot } from '../state/filter-state';
import { getColumnType } from '../state/filter-state';

/**
 * Parses anyOf value string into array of values
 */
function parseAnyOfValues(filterValue: string): string[] {
  if (!filterValue) return [];
  return filterValue
    .split(/[,\n]/)
    .map(v => v.trim())
    .filter(v => v !== '');
}

/**
 * Match single condition against data row
 */
export function matchesCondition(data: CsvRow, condition: FilterCondition): boolean {
  const value = data[condition.column];
  const filterValue = condition.value;
  const filterValue2 = condition.value2;
  const columnType = getColumnType(condition.column);

  // Handle empty operators
  if (condition.operator === 'empty') {
    return value === null || value === undefined || value === '';
  }
  if (condition.operator === 'notEmpty') {
    return value !== null && value !== undefined && value !== '';
  }

  // Handle anyOf operator (exact match only)
  if (condition.operator === 'anyOf') {
    const anyOfValues = parseAnyOfValues(filterValue);
    if (anyOfValues.length === 0) return true;

    if (columnType === 'number') {
      const numValue = Number(value);
      return anyOfValues.some(v => numValue === Number(v));
    } else {
      const strValue = value !== null && value !== undefined ? String(value).toLowerCase() : '';
      return anyOfValues.some(v => strValue === v.toLowerCase());
    }
  }

  // Handle notAnyOf operator (exact match exclusion)
  if (condition.operator === 'notAnyOf') {
    const anyOfValues = parseAnyOfValues(filterValue);
    if (anyOfValues.length === 0) return true;

    if (columnType === 'number') {
      const numValue = Number(value);
      return !anyOfValues.some(v => numValue === Number(v));
    } else {
      const strValue = value !== null && value !== undefined ? String(value).toLowerCase() : '';
      return !anyOfValues.some(v => strValue === v.toLowerCase());
    }
  }

  // For string comparison, normalize
  const strValue = value !== null && value !== undefined ? String(value).toLowerCase() : '';
  const strFilter = filterValue !== null && filterValue !== undefined ? String(filterValue).toLowerCase() : '';

  switch (condition.operator) {
    case 'equals':
      return columnType === 'number'
        ? Number(value) === Number(filterValue)
        : strValue === strFilter;
    case 'notEqual':
      return columnType === 'number'
        ? Number(value) !== Number(filterValue)
        : strValue !== strFilter;
    case 'contains':
      return strValue.includes(strFilter);
    case 'notContains':
      return !strValue.includes(strFilter);
    case 'startsWith':
      return strValue.startsWith(strFilter);
    case 'endsWith':
      return strValue.endsWith(strFilter);
    case 'greaterThan':
      return Number(value) > Number(filterValue);
    case 'lessThan':
      return Number(value) < Number(filterValue);
    case 'greaterThanOrEqual':
      return Number(value) >= Number(filterValue);
    case 'lessThanOrEqual':
      return Number(value) <= Number(filterValue);
    case 'between': {
      const num = Number(value);
      return num >= Number(filterValue) && num <= Number(filterValue2);
    }
    default:
      return true;
  }
}

/**
 * Recursively evaluate filter tree against data row
 */
export function evaluateNode(data: CsvRow, node: FilterNode): boolean {
  if (node.type === 'condition') {
    return matchesCondition(data, node);
  }

  if (node.type === 'group') {
    // Empty groups match everything
    if (!node.children || node.children.length === 0) {
      return true;
    }

    if (node.matchMode === 'ALL') {
      return node.children.every(child => evaluateNode(data, child));
    } else {
      return node.children.some(child => evaluateNode(data, child));
    }
  }

  return true;
}

/**
 * Check if filter tree has any conditions
 */
export function hasFilterConditions(node: FilterNode = getFilterRoot()): boolean {
  if (node.type === 'condition') return true;
  if (node.type === 'group' && node.children) {
    return node.children.some(child => hasFilterConditions(child));
  }
  return false;
}

export { getFilterRoot };
